import { msalInstance } from '../index';
import { loginRequest } from '../authConfig';
import { AuthValidation } from '../utils/authValidation';

class ApiService {
    constructor() {
        if (!process.env.REACT_APP_BACKEND_URL) {
            throw new Error('REACT_APP_BACKEND_URL is not set. Please configure frontend/.env as per environment rules.');
        }
        this.baseURL = process.env.REACT_APP_BACKEND_URL;
        this.isInitialized = false;
    }

    async initialize() {
        if (this.isInitialized) return;
        
        console.log('🚀 Initializing API Service...');
        const validation = await AuthValidation.runFullValidation();
        
        if (!validation.environment || !validation.msalConfig) {
            throw new Error('Authentication configuration is invalid. Please check your environment variables.');
        }
        
        this.isInitialized = true;
        console.log('✅ API Service initialized successfully');
    }

    async getAccessToken() {
        const account = msalInstance.getActiveAccount();
        if (!account) {
            console.error('No active account found');
            throw new Error('No active account. Please sign in again.');
        }

        const request = {
            ...loginRequest,
            account: account
        };

        try {
            console.log('Attempting silent token acquisition...');
            const response = await msalInstance.acquireTokenSilent(request);
            console.log('✅ Silent token acquisition successful');
            return response.accessToken;
        } catch (error) {
            console.warn('Silent token acquisition failed:', error.message);
            console.log('Attempting popup token acquisition...');
            try {
                const response = await msalInstance.acquireTokenPopup(request);
                console.log('✅ Popup token acquisition successful');
                return response.accessToken;
            } catch (popupError) {
                console.error('❌ Both silent and popup token acquisition failed:', popupError);
                throw new Error('Failed to acquire access token. Please sign in again.');
            }
        }
    }

    async makeRequest(endpoint, options = {}) {
        // Ensure API service is initialized
        await this.initialize();
        
        console.log(`🌐 Making API request: ${options.method || 'GET'} ${endpoint}`);
        
        let token;
        try {
            token = await this.getAccessToken();
            console.log('🔑 Token acquired for request');
        } catch (error) {
            console.error('❌ Failed to get access token:', error);
            throw new Error('Authentication required. Please sign in again.');
        }
        
        const config = {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
                ...options.headers
            },
            ...options
        };

        console.log('📤 Request config:', {
            url: `${this.baseURL}${endpoint}`,
            method: config.method,
            hasAuth: !!config.headers.Authorization,
            headers: Object.keys(config.headers)
        });

        const response = await fetch(`${this.baseURL}${endpoint}`, config);
        
        console.log(`📥 Response: ${response.status} ${response.statusText}`);
        
        if (!response.ok) {
            if (response.status === 401) {
                console.log('🔄 Got 401, attempting token refresh...');
                try {
                    const account = msalInstance.getActiveAccount();
                    if (!account) {
                        throw new Error('No active account for token refresh');
                    }
                    
                    const refreshRequest = {
                        ...loginRequest,
                        account: account,
                        forceRefresh: true
                    };
                    
                    const refreshed = await msalInstance.acquireTokenSilent(refreshRequest);
                    console.log('✅ Token refreshed successfully');
                    
                    const retryConfig = {
                        ...config,
                        headers: { 
                            ...config.headers, 
                            Authorization: `Bearer ${refreshed.accessToken}` 
                        }
                    };
                    
                    console.log('🔄 Retrying request with new token...');
                    const retry = await fetch(`${this.baseURL}${endpoint}`, retryConfig);
                    
                    if (!retry.ok) {
                        let errText = retry.statusText;
                        try { 
                            const errJson = await retry.json();
                            errText = errJson.detail || JSON.stringify(errJson);
                        } catch {}
                        console.error('❌ Retry failed:', retry.status, errText);
                        throw new Error(`API request failed after token refresh: ${retry.status} ${errText}`);
                    }
                    
                    console.log('✅ Retry successful');
                    const rtext = await retry.text();
                    return rtext ? JSON.parse(rtext) : {};
                } catch (refreshError) {
                    console.error('❌ Token refresh failed:', refreshError);
                    throw new Error('Authentication failed. Please sign in again.');
                }
            }
            
            let errText = response.statusText;
            try {
                const errJson = await response.json();
                errText = errJson.detail || JSON.stringify(errJson);
            } catch {}
            
            console.error('❌ API request failed:', response.status, errText);
            throw new Error(`API request failed: ${response.status} ${errText}`);
        }

        const text = await response.text();
        const result = text ? JSON.parse(text) : {};
        console.log('✅ Request successful');
        return result;
    }

    // Generic HTTP methods for convenience
    async get(endpoint, options = {}) {
        return this.makeRequest(endpoint, { ...options, method: 'GET' });
    }

    async post(endpoint, data = null, options = {}) {
        const config = { ...options, method: 'POST' };
        if (data) {
            config.body = typeof data === 'string' ? data : JSON.stringify(data);
        }
        return this.makeRequest(endpoint, config);
    }

    async patch(endpoint, data = null, options = {}) {
        const config = { ...options, method: 'PATCH' };
        if (data) {
            config.body = typeof data === 'string' ? data : JSON.stringify(data);
        }
        return this.makeRequest(endpoint, config);
    }

    async put(endpoint, data = null, options = {}) {
        const config = { ...options, method: 'PUT' };
        if (data) {
            config.body = typeof data === 'string' ? data : JSON.stringify(data);
        }
        return this.makeRequest(endpoint, config);
    }

    async delete(endpoint, options = {}) {
        return this.makeRequest(endpoint, { ...options, method: 'DELETE' });
    }

    async request(method, endpoint, data = null, options = {}) {
        const config = { ...options, method: method.toUpperCase() };
        if (data && ['POST', 'PUT', 'PATCH'].includes(config.method)) {
            config.body = typeof data === 'string' ? data : JSON.stringify(data);
        }
        return this.makeRequest(endpoint, config);
    }

    // Employees
    async getEmployees(skip = 0, limit = 100, search = '', schoolKey = '') {
        const params = new URLSearchParams({ skip, limit });
        if (search) params.append('search', search);
        if (schoolKey) params.append('school_key', schoolKey);
        return this.makeRequest(`/api/employees?${params}`);
    }

    async getEmployeeSuggestions(query) {
        const params = new URLSearchParams({ query });
        return this.makeRequest(`/api/employees/suggest?${params}`);
    }

    // Schools
    async getSchools() {
        return this.makeRequest('/api/schools');
    }

    async addSchool({ name, abbreviation = '', school_number = '' }) {
        return this.makeRequest('/api/schools', {
            method: 'POST',
            body: JSON.stringify({ name, abbreviation, school_number })
        });
    }

    // Absences & Observations (unchanged)
    async createEmployee(employeeData) { return this.makeRequest('/api/employees', { method: 'POST', body: JSON.stringify(employeeData) }); }
    async getEmployee(employeeId) { return this.makeRequest(`/api/employees/${employeeId}`); }
    async updateEmployee(employeeId, data) { return this.makeRequest(`/api/employees/${employeeId}`, { method: 'PATCH', body: JSON.stringify(data) }); }
    async bulkAssignSchool(ids, school_id) { return this.makeRequest('/api/employees/assign_school', { method: 'POST', body: JSON.stringify({ ids, school_id }) }); }
    async getEmployeesCount(search = '', schoolKey = '') {
        const params = new URLSearchParams();
        if (search) params.append('search', search);
        if (schoolKey) params.append('school_key', schoolKey);
        return this.makeRequest(`/api/employees/count?${params}`);
    }
    async getUserProfile() { return this.makeRequest('/api/user/profile'); }
    // Absences & Attendance Management
    async createAbsence(absenceData) { 
        return this.makeRequest('/api/absences', { method: 'POST', body: JSON.stringify(absenceData) }); 
    }
    
    async getAbsences(skip = 0, limit = 100, filters = {}) {
        const params = new URLSearchParams({ skip, limit });
        Object.entries(filters).forEach(([key, value]) => {
            if (value) params.append(key, value);
        });
        return this.makeRequest(`/api/absences?${params}`);
    }
    
    async getEmployeeAbsences(employeeId, year = null) { 
        const params = new URLSearchParams();
        if (year) params.append('year', year);
        return this.makeRequest(`/api/absences/employee/${employeeId}?${params}`); 
    }
    
    async updateAbsence(absenceId, updateData) {
        return this.makeRequest(`/api/absences/${absenceId}`, { method: 'PATCH', body: JSON.stringify(updateData) });
    }
    
    async editAbsence(absenceId, absenceData) {
        return this.makeRequest(`/api/absences/${absenceId}`, { method: 'PUT', body: JSON.stringify(absenceData) });
    }
    
    async deleteAbsence(absenceId) {
        return this.makeRequest(`/api/absences/${absenceId}`, { method: 'DELETE' });
    }

    // Audit Logs
    async getAuditLogs(skip = 0, limit = 100, filters = {}) {
        const params = new URLSearchParams({ skip, limit });
        Object.entries(filters).forEach(([key, value]) => {
            if (value) params.append(key, value);
        });
        return this.makeRequest(`/api/audit/logs?${params}`);
    }
    
    async getEmployeeAuditLogs(employeeId) {
        return this.makeRequest(`/api/audit/logs/employee/${employeeId}`);
    }

    // School Year Settings
    async getSchoolYearConfig() {
        return this.makeRequest('/api/settings/school-year');
    }
    
    async updateSchoolYearConfig(config) {
        return this.makeRequest('/api/settings/school-year', { method: 'POST', body: JSON.stringify(config) });
    }
    
    async getPendingAbsences() {
        return this.makeRequest('/api/absences/pending');
    }
    
    async getAbsenceStats(year = null) {
        const params = year ? new URLSearchParams({ year }) : '';
        return this.makeRequest(`/api/absences/stats?${params}`);
    }

    // ========================= Absence Calendar Endpoints =========================
    // Requirements: 8.1, 8.2, 8.3 - Absence calendar integration

    /**
     * Get absence calendar events for a date range
     * Requirement: 8.1 - Frontend calls /api/absences/calendar endpoint
     * Requirement: 8.2 - System shows all absences within the date range
     * @param {string} startDate - Start date (YYYY-MM-DD)
     * @param {string} endDate - End date (YYYY-MM-DD)
     * @param {Object} filters - Optional filters (employee_ids, departments, leave_types)
     * @returns {Promise<Array>} List of absence calendar events
     */
    async getAbsenceCalendar(startDate, endDate, filters = {}) {
        const params = new URLSearchParams({
            start_date: startDate,
            end_date: endDate
        });
        
        if (filters.employee_ids) {
            params.append('employee_ids', filters.employee_ids);
        }
        if (filters.departments) {
            params.append('departments', filters.departments);
        }
        if (filters.leave_types) {
            params.append('leave_types', filters.leave_types);
        }
        
        return this.makeRequest(`/api/absences/calendar?${params}`);
    }

    /**
     * Check for absence conflicts for an employee
     * Requirement: 8.3 - System calls /api/absences/conflicts/{employee_id}
     * @param {string} employeeId - Employee ID to check conflicts for
     * @param {string} startDate - Proposed start date (YYYY-MM-DD)
     * @param {string} endDate - Proposed end date (YYYY-MM-DD)
     * @returns {Promise<Object>} Conflict check result with has_conflicts, conflicts array, conflict_count
     */
    async checkAbsenceConflicts(employeeId, startDate, endDate) {
        const params = new URLSearchParams({
            start_date: startDate,
            end_date: endDate
        });
        return this.makeRequest(`/api/absences/conflicts/${employeeId}?${params}`);
    }

    // Notifications
    // Requirements: 6.2, 6.3, 6.5 - Notification system integration
    
    /**
     * Get notifications for the current user
     * @param {number} limit - Maximum number of notifications to return
     * @param {boolean} unreadOnly - Whether to return only unread notifications
     * @returns {Promise<Array>} List of notifications
     */
    async getNotifications(limit = 20, unreadOnly = false) {
        const params = new URLSearchParams({ limit });
        if (unreadOnly) params.append('unread_only', 'true');
        return this.makeRequest(`/api/notifications?${params}`);
    }
    
    /**
     * Mark a single notification as read
     * Requirement: 6.2 - Frontend calls /api/notifications/{id}/read
     * @param {string} notificationId - Notification ID to mark as read
     * @returns {Promise<Object>} Updated notification
     */
    async markNotificationRead(notificationId) {
        return this.makeRequest(`/api/notifications/${notificationId}`, { 
            method: 'PATCH',
            body: JSON.stringify({ read: true })
        });
    }
    
    /**
     * Mark all notifications as read for the current user
     * Requirement: 6.3 - Frontend calls /api/notifications/mark-all-read
     * @param {string} category - Optional category to filter which notifications to mark as read
     * @returns {Promise<Object>} Result with count of marked notifications
     */
    async markAllNotificationsRead(category = null) {
        const body = category ? { category } : {};
        return this.makeRequest('/api/notifications/mark-all-read', { 
            method: 'POST',
            body: JSON.stringify(body)
        });
    }
    
    /**
     * Dismiss a notification
     * @param {string} notificationId - Notification ID to dismiss
     * @returns {Promise<Object>} Updated notification
     */
    async dismissNotification(notificationId) {
        return this.makeRequest(`/api/notifications/${notificationId}`, { 
            method: 'PATCH',
            body: JSON.stringify({ dismissed: true })
        });
    }
    
    /**
     * Get count of unread notifications
     * @returns {Promise<Object>} Object with unread_count property
     */
    async getUnreadNotificationCount() {
        return this.makeRequest('/api/notifications/unread-count');
    }
    // Observations
    async createObservation(observationData) { 
        return this.makeRequest('/api/observations', { method: 'POST', body: JSON.stringify(observationData) }); 
    }
    
    async getObservations(params = {}) {
        const searchParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
            if (value !== null && value !== undefined && value !== '') {
                searchParams.append(key, value);
            }
        });
        return this.makeRequest(`/api/observations?${searchParams}`);
    }
    
    async getObservation(observationId) {
        return this.makeRequest(`/api/observations/${observationId}`);
    }
    
    async updateObservation(observationId, updateData) {
        return this.makeRequest(`/api/observations/${observationId}`, { 
            method: 'PUT', 
            body: JSON.stringify(updateData) 
        });
    }
    
    async deleteObservation(observationId) {
        return this.makeRequest(`/api/observations/${observationId}`, { method: 'DELETE' });
    }
    
    async getEmployeeObservations(employeeId) { 
        return this.makeRequest(`/api/employees/${employeeId}/observations`); 
    }
    
    async getEmployeeObservationSummary(employeeId) {
        return this.makeRequest(`/api/observations/employee/${employeeId}/summary`);
    }
    
    async createFormalObservation(employeeId, formalData, observedAt = null) {
        return this.makeRequest('/api/observations/formal', {
            method: 'POST',
            body: JSON.stringify({
                employee_id: employeeId,
                formal_data: formalData,
                observed_at: observedAt
            })
        });
    }
    
    async createQuickObservation(employeeId, quickData, observedAt = null) {
        return this.makeRequest('/api/observations/quick', {
            method: 'POST',
            body: JSON.stringify({
                employee_id: employeeId,
                quick_data: quickData,
                observed_at: observedAt
            })
        });
    }
    
    async getObservationAnalytics(params = {}) {
        const searchParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
            if (value !== null && value !== undefined && value !== '') {
                searchParams.append(key, value);
            }
        });
        return this.makeRequest(`/api/observations/analytics/sentiment-distribution?${searchParams}`);
    }
    async getDashboardStats() { return this.makeRequest('/api/dashboard/stats'); }

    // ClassLink Sync
    async syncCheck() { return this.makeRequest('/api/sync/classlink/check'); }
    async syncRun({ tables = null, sample_limit = 100 } = {}) {
        const payload = {};
        if (tables) payload.tables = tables;
        payload.sample_limit = sample_limit;
        return this.makeRequest('/api/sync/classlink/run', { method: 'POST', body: JSON.stringify({ request: payload }) });
    }
    async getSyncSchedule() { return this.makeRequest('/api/sync/classlink/schedule'); }
    async setSyncSchedule(enabled, time_utc) { return this.makeRequest('/api/sync/classlink/schedule', { method: 'POST', body: JSON.stringify({ enabled, time_utc }) }); }
    async getSyncHistory(limit = 20) { return this.makeRequest(`/api/sync/classlink/history?limit=${limit}`); }
    async getSyncStatus() { return this.makeRequest('/api/sync/classlink/status'); }

    // Mapping overrides
    async getMapping() { return this.makeRequest('/api/sync/classlink/mapping'); }
    async setMapping(mapping) { return this.makeRequest('/api/sync/classlink/mapping', { method: 'POST', body: JSON.stringify(mapping) }); }
    async getSampleColumns() { return this.makeRequest('/api/sync/classlink/columns'); }

    // Auth debug
    async authDebug() { return this.makeRequest('/api/auth/debug'); }

    // Reports
    async generateReport(reportRequest) {
        return this.makeRequest('/api/reports/generate', {
            method: 'POST',
            body: JSON.stringify(reportRequest)
        });
    }

    async getReports(params = {}) {
        const searchParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
            if (value !== null && value !== undefined && value !== '') {
                searchParams.append(key, value);
            }
        });
        return this.makeRequest(`/api/reports?${searchParams}`);
    }

    async getReport(reportId) {
        return this.makeRequest(`/api/reports/${reportId}`);
    }

    async deleteReport(reportId) {
        return this.makeRequest(`/api/reports/${reportId}`, { method: 'DELETE' });
    }

    async exportReport(reportId, format) {
        return this.makeRequest(`/api/reports/${reportId}/export/${format}`, { method: 'POST' });
    }

    async getReportTemplates() {
        return this.makeRequest('/api/reports/templates/');
    }

    async getDashboardAnalytics() {
        return this.makeRequest('/api/reports/analytics/dashboard');
    }

    // Scheduled Reports
    async createScheduledReport(scheduledReport) {
        return this.makeRequest('/api/reports/scheduled', {
            method: 'POST',
            body: JSON.stringify(scheduledReport)
        });
    }

    async getScheduledReports() {
        return this.makeRequest('/api/reports/scheduled/');
    }

    async updateScheduledReport(reportId, updates) {
        return this.makeRequest(`/api/reports/scheduled/${reportId}`, {
            method: 'PUT',
            body: JSON.stringify(updates)
        });
    }

    async deleteScheduledReport(reportId) {
        return this.makeRequest(`/api/reports/scheduled/${reportId}`, { method: 'DELETE' });
    }

    // ========================= Auth Endpoints =========================
    // Requirement: 7.1, 7.4 - Frontend auth integration

    /**
     * Get current user's info including resolved roles and permissions
     * Requirement: 7.1 - Frontend receives user's resolved roles and permissions
     * @returns {Promise<Object>} User info with oid, email, name, tenant_id, roles, permissions, source_groups
     */
    async getCurrentUser() {
        return this.makeRequest('/api/auth/me');
    }

    /**
     * Refresh user's roles by fetching fresh data from backend
     * Requirement: 7.4 - Frontend provides method to refresh role information
     * @returns {Promise<Object>} Updated user info with roles and permissions
     */
    async refreshRoles() {
        // Force a fresh fetch of user roles from the backend
        // The backend will re-resolve roles from Azure AD group memberships
        return this.makeRequest('/api/auth/me');
    }

    // ========================= Group Mapping Admin Endpoints =========================
    
    /**
     * Get all group mappings (admin only)
     * @returns {Promise<Array>} List of group mappings
     */
    async getGroupMappings() {
        return this.makeRequest('/api/admin/group-mappings/');
    }

    /**
     * Create a new group mapping (admin only)
     * @param {Object} mapping - Group mapping data
     * @returns {Promise<Object>} Created group mapping
     */
    async createGroupMapping(mapping) {
        return this.makeRequest('/api/admin/group-mappings/', {
            method: 'POST',
            body: JSON.stringify(mapping)
        });
    }

    /**
     * Update a group mapping (admin only)
     * @param {string} mappingId - Mapping ID
     * @param {Object} updates - Update data
     * @returns {Promise<Object>} Updated group mapping
     */
    async updateGroupMapping(mappingId, updates) {
        return this.makeRequest(`/api/admin/group-mappings/${mappingId}`, {
            method: 'PUT',
            body: JSON.stringify(updates)
        });
    }

    /**
     * Delete a group mapping (admin only)
     * @param {string} mappingId - Mapping ID
     * @returns {Promise<Object>} Deletion result
     */
    async deleteGroupMapping(mappingId) {
        return this.makeRequest(`/api/admin/group-mappings/${mappingId}`, { method: 'DELETE' });
    }

    // ========================= Azure Config Admin Endpoints =========================

    /**
     * Get Azure AD configuration (admin only)
     * @returns {Promise<Object>} Azure AD config (with masked secret)
     */
    async getAzureConfig() {
        return this.makeRequest('/api/admin/azure-config');
    }

    /**
     * Update Azure AD configuration (admin only)
     * @param {Object} config - Azure AD config data
     * @returns {Promise<Object>} Updated config
     */
    async updateAzureConfig(config) {
        return this.makeRequest('/api/admin/azure-config', {
            method: 'PUT',
            body: JSON.stringify(config)
        });
    }

    /**
     * Test Azure AD connectivity (admin only)
     * @returns {Promise<Object>} Connectivity test result
     */
    async testAzureConnectivity() {
        return this.makeRequest('/api/admin/azure-config/test', { method: 'POST' });
    }

    // ========================= System Monitoring Endpoints =========================
    // Requirements: 3.1, 3.2, 3.3, 3.4 - System monitoring dashboard integration

    /**
     * Get overall system health status
     * Requirement: 3.1 - Frontend calls /api/admin/system/health endpoint
     * @returns {Promise<Object>} System health including CPU, memory, disk, database status
     */
    async getSystemHealth() {
        return this.makeRequest('/api/admin/system/health');
    }

    /**
     * Get system performance metrics
     * Requirement: 3.2 - Frontend calls /api/admin/system/performance endpoint
     * @returns {Promise<Object>} Performance metrics including uptime, process count, network I/O
     */
    async getSystemPerformance() {
        return this.makeRequest('/api/admin/system/performance');
    }

    /**
     * Get system alerts with optional filtering
     * Requirement: 3.3 - Frontend fetches from /api/admin/system/alerts endpoint
     * @param {Object} params - Query parameters (page_size, resolved, etc.)
     * @returns {Promise<Object>} System alerts with pagination
     */
    async getSystemAlerts(params = {}) {
        const searchParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
            if (value !== null && value !== undefined && value !== '') {
                searchParams.append(key, value);
            }
        });
        const queryString = searchParams.toString();
        return this.makeRequest(`/api/admin/system/alerts${queryString ? `?${queryString}` : ''}`);
    }

    /**
     * Resolve a system alert
     * Requirement: 3.4 - Frontend calls /api/admin/system/alerts/{id}/resolve
     * @param {string} alertId - Alert ID to resolve
     * @returns {Promise<Object>} Resolution result
     */
    async resolveSystemAlert(alertId) {
        return this.makeRequest(`/api/admin/system/alerts/${alertId}/resolve`, { method: 'POST' });
    }

    // ========================= Security Endpoints =========================
    // Requirements: 4.1, 4.2, 4.3, 4.4, 4.5 - Security dashboard integration

    /**
     * Get overall security status including threat level
     * Requirement: 4.1 - Frontend calls /api/security/security-status endpoint
     * @returns {Promise<Object>} Security status with threat level, incident counts, severity counts
     */
    async getSecurityStatus() {
        return this.makeRequest('/api/security/security-status');
    }

    /**
     * Get security incidents
     * Requirement: 4.2 - Frontend fetches from /api/security/incidents endpoint
     * @param {Object} params - Query parameters (limit, status, severity, etc.)
     * @returns {Promise<Object>} Security incidents list
     */
    async getSecurityIncidents(params = {}) {
        const searchParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
            if (value !== null && value !== undefined && value !== '') {
                searchParams.append(key, value);
            }
        });
        const queryString = searchParams.toString();
        return this.makeRequest(`/api/security/incidents${queryString ? `?${queryString}` : ''}`);
    }

    /**
     * Get list of blocked IP addresses
     * Requirement: 4.3 - Frontend fetches from /api/security/blocked-ips endpoint
     * @returns {Promise<Object>} List of blocked IPs
     */
    async getBlockedIPs() {
        return this.makeRequest('/api/security/blocked-ips');
    }

    /**
     * Unblock an IP address
     * Requirement: 4.4 - Frontend calls /api/security/blocked-ips/{ip}/unblock
     * @param {string} ipAddress - IP address to unblock
     * @param {string} reason - Reason for unblocking
     * @returns {Promise<Object>} Unblock result
     */
    async unblockIP(ipAddress, reason = '') {
        return this.makeRequest(`/api/security/blocked-ips/${encodeURIComponent(ipAddress)}/unblock`, {
            method: 'POST',
            body: JSON.stringify({ reason })
        });
    }

    /**
     * Update security incident status
     * Requirement: 4.5 - Frontend calls /api/security/incidents/{id}/status
     * @param {string} incidentId - Incident ID
     * @param {string} status - New status
     * @param {string} notes - Optional notes
     * @returns {Promise<Object>} Update result
     */
    async updateIncidentStatus(incidentId, status, notes = '') {
        return this.makeRequest(`/api/security/incidents/${incidentId}/status`, {
            method: 'PUT',
            body: JSON.stringify({ status, notes })
        });
    }

    // ========================= User Management Endpoints =========================
    // Requirements: 5.1, 5.2, 5.3, 5.4 - User management integration

    /**
     * Get users with pagination and filtering
     * Requirement: 5.1 - Frontend calls /api/admin/users endpoint
     * @param {Object} params - Query parameters (page, page_size, role, status, search)
     * @returns {Promise<Object>} Users list with pagination
     */
    async getUsers(params = {}) {
        const searchParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
            if (value !== null && value !== undefined && value !== '') {
                searchParams.append(key, value);
            }
        });
        const queryString = searchParams.toString();
        return this.makeRequest(`/api/admin/users${queryString ? `?${queryString}` : ''}`);
    }

    /**
     * Create a new user
     * Requirement: 5.2 - Frontend submits to user creation endpoint
     * @param {Object} userData - User data (email, first_name, last_name, role, etc.)
     * @returns {Promise<Object>} Created user
     */
    async createUser(userData) {
        return this.makeRequest('/api/admin/users', {
            method: 'POST',
            body: JSON.stringify(userData)
        });
    }

    /**
     * Deactivate a user account
     * Requirement: 5.3 - Frontend calls /api/admin/users/{id}/deactivate
     * @param {string} userId - User ID to deactivate
     * @param {string} reason - Optional reason for deactivation
     * @returns {Promise<Object>} Deactivation result
     */
    async deactivateUser(userId, reason = '') {
        return this.makeRequest(`/api/admin/users/${userId}/deactivate`, {
            method: 'POST',
            body: JSON.stringify({ reason })
        });
    }

    /**
     * Reactivate a user account
     * Requirement: 5.4 - Frontend calls /api/admin/users/{id}/reactivate
     * @param {string} userId - User ID to reactivate
     * @returns {Promise<Object>} Reactivation result
     */
    async reactivateUser(userId) {
        return this.makeRequest(`/api/admin/users/${userId}/reactivate`, { method: 'POST' });
    }

    // ========================= Performance Endpoints =========================
    // Requirements: 1.1, 1.3 - Performance view integration

    /**
     * Get performance metrics based on observations
     * Requirement: 1.3 - System retrieves observation-based performance scores
     * @param {Object} params - Query parameters (start_date, end_date, employee_id, etc.)
     * @returns {Promise<Object>} Performance metrics
     */
    async getPerformanceMetrics(params = {}) {
        const searchParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
            if (value !== null && value !== undefined && value !== '') {
                searchParams.append(key, value);
            }
        });
        const queryString = searchParams.toString();
        return this.makeRequest(`/api/performance/employee-metrics${queryString ? `?${queryString}` : ''}`);
    }

    /**
     * Get top performing employees based on observation scores
     * Requirement: 1.1 - Frontend fetches performance data from backend API
     * @param {number} limit - Number of top performers to return (default: 10)
     * @returns {Promise<Object>} Top performers list
     */
    async getTopPerformers(limit = 10) {
        return this.makeRequest(`/api/performance/top-performers?limit=${limit}`);
    }

    // ========================= AI Conversation Endpoints =========================
    // Requirements: 9.1, 9.2, 9.3, 9.4 - AI chat integration

    /**
     * Send a message to the AI assistant
     * Requirement: 9.1 - Frontend submits to AI conversation endpoint
     * Requirement: 9.2 - Frontend displays AI response in chat interface
     * @param {string} message - User message to send
     * @param {Object} context - Optional context including session_id
     * @returns {Promise<Object>} AI response with message, actions, and session_id
     */
    async sendAIChatMessage(message, context = {}) {
        return this.makeRequest('/api/ai/chat', {
            method: 'POST',
            body: JSON.stringify({
                message: message,
                context: context
            })
        });
    }

    /**
     * Get AI conversation history
     * Requirement: 9.4 - AI maintains conversation context across messages
     * @param {string} sessionId - Optional session ID to get specific session history
     * @param {number} limit - Maximum number of messages to return
     * @returns {Promise<Object>} Conversation history
     */
    async getAIConversationHistory(sessionId = null, limit = 50) {
        const params = new URLSearchParams({ limit });
        if (sessionId) params.append('session_id', sessionId);
        return this.makeRequest(`/api/ai/conversations/history?${params}`);
    }

    /**
     * Clear AI conversation context and history
     * @param {string} sessionId - Optional session ID to clear specific session
     * @returns {Promise<Object>} Clear result
     */
    async clearAIConversation(sessionId = null) {
        return this.makeRequest('/api/ai/conversations/clear', {
            method: 'DELETE',
            body: JSON.stringify({ session_id: sessionId })
        });
    }

    /**
     * Get AI conversation context for a specific session
     * Requirement: 9.4 - AI maintains conversation context across messages
     * @param {string} sessionId - Session ID to get context for
     * @returns {Promise<Object>} Conversation context
     */
    async getAIConversationContext(sessionId) {
        return this.makeRequest(`/api/ai/context/${sessionId}`);
    }

    /**
     * Get user's recent AI conversation sessions
     * @returns {Promise<Object>} List of recent sessions
     */
    async getAIConversationSessions() {
        return this.makeRequest('/api/ai/conversations/sessions');
    }

    /**
     * Execute an AI-suggested action
     * Requirement: 9.3 - System provides actionable buttons for AI suggestions
     * @param {Object} action - Action object with type and data
     * @returns {Promise<Object>} Action execution result
     */
    async executeAIAction(action) {
        // Actions are typically handled by sending a follow-up message
        // or calling specific endpoints based on action type
        const actionMessage = action.label || action.type;
        return this.sendAIChatMessage(actionMessage, { action: action });
    }
}

export const apiService = new ApiService();
export default apiService;